import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TOptions } from 'i18next';

export type TranslateMapConfig = Record<
  string,
  {
    id: string;
    default: string;
    options?: TOptions<any> | string;
  }
>;

function hasValidKeys<K extends keyof TranslateMapConfig>(keys: K[]) {
  return keys.filter(key => key && key !== '').length;
}

export function useTranslateMap<K extends keyof TranslateMapConfig>(
  map: TranslateMapConfig,
  keys?: K[],
  optionsGetter?: (key: K) => TranslateMapConfig[keyof TranslateMapConfig]['options']
) {
  const { t: translate, i18n } = useTranslation();

  const translateKeys = useCallback(
    (keys: K[]) =>
      keys.reduce(
        (result, key) => ({
          ...result,
          [key]: map[key] ? translate(map[key].id, map[key].default, map[key].options ?? optionsGetter?.(key)) : key
        }),
        {} as Record<K, string>
      ),
    [translate, map, i18n.language, optionsGetter]
  );

  return useMemo(() => {
    // Make sure that the keys array has valid items before calculating
    if (keys && !hasValidKeys(keys)) {
      return {} as Record<string, string>;
    }
    const keysToTranslate = keys ? keys : (Object.keys(map) as K[]);
    return translateKeys(keysToTranslate);
  }, [map, keys, translateKeys]);
}

export function useTranslateMapItem(
  map: TranslateMapConfig,
  key: keyof TranslateMapConfig,
  optionsGetter?: () => TranslateMapConfig[keyof TranslateMapConfig]['options']
) {
  const value = useTranslateMap(map, [key], optionsGetter);
  return value ? value[key] : value;
}
